import type { BarChartData, SingleValue, SortOption } from '@/core';

export function calculatePercentages(data: BarChartData[]) {
  const { sum, comparisonSum } = data.reduce(
    (accumulator, { value, comparisonValue }) => ({
      sum: accumulator.sum + (value ?? 0),
      comparisonSum: accumulator.comparisonSum + (comparisonValue ?? 0),
    }),
    { sum: 0, comparisonSum: 0 }
  );

  return data.map((seriesData) => {
    const percentage = sum > 0 ? ((seriesData.value ?? 0) / sum) * 100 : 0;
    const comparisonPercentage =
      seriesData.comparisonValue !== undefined && comparisonSum > 0
        ? ((seriesData.comparisonValue ?? 0) / comparisonSum) * 100
        : seriesData.comparisonValue;

    return {
      ...seriesData,
      value: percentage,
      comparisonValue: comparisonPercentage,
    };
  });
}

/**
 * sortTopListSeriesData mutates the original data. Switching between default and asc/desc sort works here
 * because BarChartPanel generates a new array before calling this function.
 */
export function sortTopListSeriesData(data: BarChartData[], sortOrder: SortOption = 'default') {
  if (sortOrder === 'asc') {
    // Smallest values at the top (nulls sink to bottom)
    return data.sort((a, b) => {
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      return a.value - b.value;
    });
  } else if (sortOrder === 'desc') {
    // Largest values at the top (nulls sink to bottom)
    return data.sort((a, b) => {
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      return b.value - a.value;
    });
  } else if (sortOrder === 'series-name-asc') {
    return data.sort((a, b) => a.label.localeCompare(b.label));
  } else if (sortOrder === 'series-name-desc') {
    return data.sort((a, b) => b.label.localeCompare(a.label));
  }
  return data;
}

/**
 * Links a comparison value to it's corresponding baseline series
 */
export function linkComparisonSeries(barChartData: BarChartData[], currentSeries: SingleValue) {
  if (currentSeries.config?.compareTo !== undefined && currentSeries.config?.compareTo !== null) {
    const baselineSeries = barChartData[currentSeries.config.compareTo];

    if (baselineSeries) {
      barChartData[currentSeries.config.compareTo] = {
        ...baselineSeries,
        comparisonValue: currentSeries.value,
      }; // go back and update baselineSeries to include the comparison value
    }
  }
}
